import "./LabTestAndPanelEditComponent.scss";
import React, {useCallback, useEffect} from "react";
import {useSelector} from "react-redux";
import commonService from "../../../../../shared/services/common.service";
import TabsWrapperComponent, {
    TabComponent,
    TabContentComponent,
    TabsComponent
} from "../../../../../shared/components/tabs/tabsComponent";
import CheckBoxComponent from "../../../../../shared/components/core/checkbox/CheckboxComponent";
import StatusCardComponent from "../../../../../shared/components/status-card/statusCardComponent";
import DrawerComponent from "../../../../../shared/components/drawer/drawerComponent";
import ButtonComponent from "../../../../../shared/components/core/button/ButtonComponent";
import TestDetailsComponent from "../../../../../shared/components/test-details/TestDetailsComponent";

interface LabTestAndPanelEditComponentProps {
    labTestAndPanel: any;
    canEdit: boolean;
    onEditCancel: () => void;
    onUpdateComplete: Function;
    labID: string;
}

const TESTS = 'tests';
const TEST_PANELS = 'test_panels';
const LabTestAndPanelEditComponent = (props: LabTestAndPanelEditComponentProps) => {
    const {labTestAndPanel, labID, onEditCancel, onUpdateComplete} = props;

    const [isEdited, setIsEdited] = React.useState(false);
    const [activeTab, setActiveTab] = React.useState<typeof TESTS|typeof TEST_PANELS>(TESTS);
    const {testListLite, testPanelList, biomarkerList} = useSelector((state: any) => state.staticData);
    const [viewDetailsDrawerOpen, setViewDetailsDrawerOpen] = React.useState(false);
    const [testOrPanelDetails, setTestOrPanelDetails] = React.useState<any>(undefined); // [testOrPanelDetails, setTestOrPanelDetails
    const [isSubmitting, setIsSubmitting] = React.useState(false);
    const [selectedTestAndTestPanelIds, setSelectedTestAndTestPanelIds] = React.useState<any>({
        testIds: [],
        panelIds: []
    });

    useEffect(() => {
        if (labTestAndPanel?.panelDetails?.length > 0 || labTestAndPanel?.testDetails?.length > 0) {
            setIsEdited(true);
            setSelectedTestAndTestPanelIds(() => {
                return {
                    testIds: labTestAndPanel?.testDetails?.map((test: any) => test?.testId),
                    panelIds: labTestAndPanel?.panelDetails?.map((panel: any) => panel?.panelId)
                }
            });
        }
    }, [labTestAndPanel]);

    const handleActiveTabChange = useCallback((tab: any) => {
        setActiveTab(tab);
    }, []);

    const handleViewDetailsDrawerOpen = useCallback((details: any) => {
        if (activeTab === TEST_PANELS) {
            const testNames = details?.testIds?.map((testId: any) => {
                const test = testListLite?.find((test: any) => test?.id === testId);
                return test.name;
            });
            const testObject = {...details, tests: testNames};
            setTestOrPanelDetails(testObject);
        } else {
            const biomarkerNames = details?.biomarkerIds?.map((biomarkerId: any) => {
                const biomarker = biomarkerList?.find((biomarker: any) => biomarker?.id === biomarkerId);
                return biomarker?.name;
            });
            setTestOrPanelDetails({...details, biomarkers: biomarkerNames});
        }
        setViewDetailsDrawerOpen(true);
    }, [testListLite, activeTab, biomarkerList]);

    const addLabTestAndPanels = useCallback(() => {
        setIsSubmitting(true);
        commonService._lab.addLabTestAndPanels(labID, selectedTestAndTestPanelIds)
            .then((res: any) => {
                onUpdateComplete(selectedTestAndTestPanelIds);
                setIsSubmitting(false);
            })
            .catch((err: any) => {
                setIsSubmitting(false);
            });
    }, [labID, selectedTestAndTestPanelIds, onUpdateComplete]);

    return (
        <div className={'lab-test-and-panel-edit-component details-tab-data-content'}>
            <form>
                <div className="t-form-controls-wrapper assign-lab-to-test-tab">
                    <TabsWrapperComponent>
                        <TabsComponent
                            value={activeTab}
                            allowScrollButtonsMobile={false}
                            variant={"fullWidth"}
                            onUpdate={(e: any, v: any) => {
                                handleActiveTabChange(v)
                            }}
                        >
                            <TabComponent label={"TESTS"} value={"tests"}/>
                            <TabComponent label={"Test panels"} value={'test_panels'}/>
                        </TabsComponent>
                        <TabContentComponent selectedTab={activeTab} value={TESTS}>
                            {
                                testListLite && testListLite.length > 0 ?
                                    testListLite?.map((test: any, index: any) => {
                                            return (
                                                <div key={index} className={'lab-list-item'}>
                                                    <div className={'lab-list-item-name-and-selection'}>
                                                        <CheckBoxComponent
                                                            checked={selectedTestAndTestPanelIds?.testIds?.includes(test.id)}
                                                            disabled={!test?.isActive}
                                                            onUpdate={(isChecked: boolean) => {
                                                                setIsEdited(true)
                                                                if (isChecked) {

                                                                    setSelectedTestAndTestPanelIds((prevState: any) => {
                                                                            return {
                                                                                ...prevState,
                                                                                testIds: [...prevState?.testIds, test.id]
                                                                            }
                                                                        }
                                                                    )

                                                                } else {
                                                                    //remove the test from the selected list
                                                                    setSelectedTestAndTestPanelIds((prevState: any) => {
                                                                            return {
                                                                                ...prevState,
                                                                                testIds: prevState?.testIds?.filter((id: any) => id !== test.id)
                                                                            }
                                                                        }
                                                                    )
                                                                }
                                                            }}
                                                            name={commonService.capitalizeFirstLetterOfEachWord(test.name)}
                                                        />
                                                        <div className={'lab-list-item-name'}>
                                                            {commonService.capitalizeFirstLetterOfEachWord(test.name)}
                                                        </div>
                                                    </div>
                                                    <div className={'lab-list-item__action'}>
                                                        <div className={'underline-button'} onClick={() => {
                                                            handleViewDetailsDrawerOpen(test);
                                                        }}>
                                                            View Details
                                                        </div>
                                                    </div>
                                                </div>
                                            )
                                        }
                                    ) :
                                    <StatusCardComponent title={"No tests found"}/>
                            }
                        </TabContentComponent>
                        <TabContentComponent selectedTab={activeTab} value={TEST_PANELS}>
                            {
                                testPanelList && testPanelList.length > 0 ?
                                    testPanelList.map((testPanel: any, index: number) => {
                                            return (
                                                <div key={index} className={'lab-list-item'}>
                                                    <div className={'lab-list-item-name-and-selection'}>
                                                        <CheckBoxComponent
                                                            checked={selectedTestAndTestPanelIds?.panelIds?.includes(testPanel.id)}
                                                            disabled={!testPanel?.isActive}
                                                            onUpdate={(isChecked: boolean) => {
                                                                setIsEdited(true)
                                                                if (isChecked) {
                                                                    setSelectedTestAndTestPanelIds((prevState: any) => {
                                                                        return {
                                                                            ...prevState,
                                                                            panelIds: [...prevState?.panelIds, testPanel.id]
                                                                        }
                                                                    });
                                                                } else {
                                                                    //remove the test from the selected list
                                                                    setSelectedTestAndTestPanelIds((prevState: any) => {
                                                                        return {
                                                                            ...prevState,
                                                                            panelIds: prevState?.panelIds?.filter((id: any) => id !== testPanel.id)
                                                                        }
                                                                    });
                                                                }
                                                            }}
                                                            name={commonService.capitalizeFirstLetterOfEachWord(testPanel.name)}
                                                        />
                                                        <div className={'lab-list-item-name'}>
                                                            {commonService.capitalizeFirstLetterOfEachWord(testPanel.name)}
                                                        </div>
                                                    </div>
                                                    <div className={'lab-list-item__action'}>
                                                        <div className={'underline-button'} onClick={() => {
                                                            handleViewDetailsDrawerOpen(testPanel);
                                                        }}>
                                                            View Details
                                                        </div>
                                                    </div>
                                                </div>
                                            )
                                        }
                                    ) :
                                    <StatusCardComponent title={"No test panels found"}/>
                            }
                        </TabContentComponent>
                    </TabsWrapperComponent>
                </div>
                <div className='form-action-section-edit'>
                    <ButtonComponent
                        className={"width-25 "}
                        type="button"
                        color={"secondary"}
                        variant={"outlined"}
                        fullWidth={false}
                        handleClick={() => onEditCancel()}
                    >
                        Cancel
                    </ButtonComponent>
                    <ButtonComponent
                        className={"width-25"}
                        type="button"
                        color={"primary"}
                        variant={"contained"}
                        fullWidth={false}
                        isLoading={isSubmitting}
                        disabled={!isEdited}
                        handleClick={() => addLabTestAndPanels()}
                    >
                        Save
                    </ButtonComponent>
                </div>
            </form>
            <DrawerComponent
                title={''}
                open={viewDetailsDrawerOpen}
                onClose={() => {
                    setViewDetailsDrawerOpen(false)
                }}
            >
                <TestDetailsComponent TESTS={TESTS} testOrPanelDetails={testOrPanelDetails} TEST_PANELS={TEST_PANELS}
                                      activeTab={activeTab}/>
            </DrawerComponent>
        </div>
    )
        ;

};

export default LabTestAndPanelEditComponent;
